import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { API_URL } from '../config/endpoints';
import { readToken, readTokenSync, writeToken, clearToken, isNativeStorage } from '../services/tokenStorage';
import { signInWith, signOutProviders, availableProviders, initialiseSocialLogin, isSignInCancelled, type Provider, type SocialCredential, type SocialConfig } from '../services/socialAuth';

interface User {
    id: string;
    username: string;
    displayName?: string | null;
    email: string | null;
    balance: number;
    role?: 'player' | 'admin';
    /** Which sign-in methods this account has; drives the settings screen. */
    providers?: Array<'password' | Provider>;
    hasPassword?: boolean;
    canChangeUsername?: boolean;
    createdAt: number;
    lastLoginAt: number | null;
    totalBets: number;
    totalWins: number;
}

/** What the server says is configured; a button for an unconfigured provider would only fail. */
export interface ServerConfig {
    privacyUrl: string | null;
    termsUrl: string | null;
    providers: { password: boolean; apple: boolean; google: boolean };
    /** Public client identifiers for the native sign-in sheets. */
    native?: SocialConfig | null;
}

interface AuthContextType {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    /**
     * A stored session exists but the server could not be asked about it (outage
     * or no network). The token is kept and re-checked with backoff; only a 401
     * or 404 from the server signs the player out.
     */
    sessionUnverified: boolean;
    login: (usernameOrEmail: string, password: string) => Promise<{ success: boolean; error?: string }>;
    register: (username: string, email: string, password: string) => Promise<{ success: boolean; error?: string; pending?: boolean }>;
    loginWithProvider: (provider: Provider) => Promise<{ success: boolean; error?: string; isNewUser?: boolean }>;
    serverConfig: ServerConfig | null;
    socialProviders: Provider[];
    refreshUser: () => Promise<void>;
    logout: () => Promise<void>;
    endSession: () => void;
    logoutError: string | null;
    updateBalance: (newBalance: number) => void;
}

const AuthContext = createContext<AuthContextType>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
    sessionUnverified: false,
    login: async () => ({ success: false }),
    register: async () => ({ success: false }),
    loginWithProvider: async () => ({ success: false }),
    serverConfig: null,
    socialProviders: [],
    refreshUser: async () => {},
    logout: async () => {},
    endSession: () => {},
    logoutError: null,
    updateBalance: () => {}
});

export const useAuth = () => useContext(AuthContext);

/** First retry after an unverifiable startup check, doubling up to the cap. */
const VERIFY_RETRY_BASE_MS = 1000;
const VERIFY_RETRY_MAX_MS = 30000;
/**
 * Statuses that mean the server looked at this session and rejected it.
 * 401 is how authenticateHttp reports an invalid, expired or revoked session;
 * 404 from /me means the account no longer exists. 403 is only used for
 * admin-only routes, and 5xx means the server could not check (503 is
 * "verification unavailable"), so neither is proof the session is dead.
 */
const SESSION_REJECTED = new Set([401, 404]);


export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const [logoutError, setLogoutError] = useState<string | null>(null);
    const [serverConfig, setServerConfig] = useState<ServerConfig | null>(null);
    // The social buttons appear only once the native plugin has accepted its
    // configuration; before that, pressing one could only fail.
    const [nativeReady, setNativeReady] = useState(false);
    const [sessionUnverified, setSessionUnverified] = useState(false);
    const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const retryAttemptRef = useRef(0);
    const hasUserRef = useRef(false);
    // The sign-in methods of whoever is signed in, kept for the moment the
    // session ends (by then `user` may already be cleared).
    const linkedProvidersRef = useRef<User['providers']>(undefined);
    useEffect(() => {
        hasUserRef.current = user !== null;
        if (user) linkedProvidersRef.current = user.providers;
    }, [user]);
    const cancelRetry = useCallback(() => {
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
    }, []);
    useEffect(() => cancelRetry, [cancelRetry]);

    const endSession = useCallback(() => {
        cancelRetry();
        retryAttemptRef.current = 0;
        setSessionUnverified(false);
        void clearToken();
        setToken(null);
        setUser(null);
        // Also forget the Apple/Google session the native plugin keeps, so the
        // next person on this phone is not handed the previous player's account.
        const linked = linkedProvidersRef.current;
        linkedProvidersRef.current = undefined;
        void signOutProviders(linked);
    }, [cancelRetry]);

    // Load token from localStorage on mount
    useEffect(() => {
        // In the browser the token is readable synchronously, so the sign-in
        // screen paints on the first render rather than after a loading flash.
        if (!isNativeStorage()) {
            const storedToken = readTokenSync();
            if (storedToken) { setToken(storedToken); void fetchUser(storedToken); }
            else setIsLoading(false);
            return;
        }
        // Native builds keep it in app-private storage, which is async only.
        let cancelled = false;
        (async () => {
            const storedToken = await readToken();
            if (cancelled) return;
            if (storedToken) { setToken(storedToken); await fetchUser(storedToken); }
            else setIsLoading(false);
        })();
        return () => { cancelled = true; };
    }, []);

    // Which sign-in methods exist is the server's business, not a build-time flag.
    useEffect(() => {
        let cancelled = false;
        fetch(`${API_URL}/api/config`)
            .then(response => (response.ok ? response.json() : null))
            // Only accept a response that actually looks like the config contract.
            .then(config => {
                if (cancelled || !config || typeof config !== 'object' || !('providers' in config)) return;
                setServerConfig(config);
                // Configure the native sheets from the server's identifiers, so
                // changing a client ID does not mean shipping a new build.
                if (config.native) void initialiseSocialLogin(config.native, config.providers).then(ready => {
                    if (!cancelled && ready) setNativeReady(true);
                });
            })
            .catch(() => { /* the password form still works without this */ });
        return () => { cancelled = true; };
    }, []);

    // The retry timer calls back through this ref, so fetchUser captures only
    // refs and setters and stays safe to call from mount-time effects.
    const fetchUserRef = useRef<(authToken: string) => Promise<void>>(async () => {});
    const fetchUser = async (authToken: string) => {
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
        let outcome: 'ok' | 'rejected' | 'unavailable' = 'unavailable';
        try {
            const response = await fetch(`${API_URL}/api/auth/me`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });

            if (await readToken() !== authToken) return;
            if (response.ok) {
                const data = await response.json();
                if (await readToken() !== authToken) return;
                outcome = 'ok';
                setUser(data.user);
                setToken(authToken);
            } else if (SESSION_REJECTED.has(response.status)) {
                outcome = 'rejected';
            }
        } catch {
            // Network failure: the server never saw the request, so it has not
            // said anything about the session. Fall through as 'unavailable'.
            if (await readToken() !== authToken) return;
        }

        if (outcome === 'rejected') {
            retryAttemptRef.current = 0;
            setSessionUnverified(false);
            await clearToken();
            setToken(null);
            setUser(null);
            setIsLoading(false);
            return;
        }
        if (outcome === 'ok') {
            retryAttemptRef.current = 0;
            setSessionUnverified(false);
            setIsLoading(false);
            return;
        }
        // Could not check. An already-loaded player (e.g. a settings refresh)
        // just keeps their current state; on startup the token is kept, the app
        // stays on the loading screen, and the check is retried with backoff.
        if (hasUserRef.current) return;
        setSessionUnverified(true);
        const delay = Math.min(VERIFY_RETRY_MAX_MS, VERIFY_RETRY_BASE_MS * 2 ** retryAttemptRef.current);
        retryAttemptRef.current += 1;
        retryTimerRef.current = setTimeout(() => {
            retryTimerRef.current = null;
            void readToken().then(current => { if (current === authToken) void fetchUserRef.current(authToken); });
        }, delay);
    };
    fetchUserRef.current = fetchUser;

    const login = useCallback(async (usernameOrEmail: string, password: string) => {
        try {
            const response = await fetch(`${API_URL}/api/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ usernameOrEmail, password })
            });

            const data = await response.json();

            if (response.ok) {
                await writeToken(data.token);
                setToken(data.token);
                setUser(data.user);
                return { success: true };
            } else {
                return { success: false, error: data.error || 'Login failed' };
            }
        } catch (err) {
            console.error('Login error:', err);
            return { success: false, error: 'Network error' };
        }
    }, []);

    const register = useCallback(async (username: string, email: string, password: string) => {
        try {
            const response = await fetch(`${API_URL}/api/auth/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ username, email, password })
            });

            const data = await response.json();

            // Registration always answers 202 with a message and no session, for
            // a new address and an already-registered one alike, so the response
            // cannot be used to discover who has an account. The account (if new)
            // now exists; the player signs in next. `pending` tells the form to
            // switch to sign-in and show the server's message as a notice.
            if (response.status === 202) {
                return { success: false, pending: true, error: data.message || 'Account created. Please sign in.' };
            }
            if (response.ok && data.token) {
                await writeToken(data.token);
                setToken(data.token);
                setUser(data.user);
                return { success: true };
            } else {
                return { success: false, error: data.error || 'Registration failed' };
            }
        } catch (err) {
            console.error('Registration error:', err);
            return { success: false, error: 'Network error' };
        }
    }, []);

    useEffect(() => {
        const changed = (event: StorageEvent) => {
            if (event.key !== 'token' && event.key !== null) return;
            setUser(null);
            setToken(null);
            void readToken().then(current => {
                if (current) { void fetchUser(current); return; }
                // Signed out elsewhere while this tab was still waiting to verify.
                cancelRetry();
                setSessionUnverified(false);
                setIsLoading(false);
            });
        };
        window.addEventListener('storage', changed);
        return () => window.removeEventListener('storage', changed);
    }, [cancelRetry]);

    const logout = useCallback(async () => {
        setLogoutError(null);
        if (!token) { endSession(); return; }
        try {
            const response = await fetch(API_URL + '/api/auth/logout', {
                method: 'POST', headers: {Authorization: 'Bearer ' + token}
            });
            if (!response.ok && response.status !== 401) throw new Error('Logout failed');
            if (await readToken() === token) endSession();
        } catch {
            setLogoutError('Could not sign out on the server. Check your connection and retry.');
        }
    }, [token, endSession]);

    const updateBalance = useCallback((newBalance: number) => {
        setUser(prev => prev ? { ...prev, balance: newBalance } : null);
    }, []);

    /**
     * Sign in through Apple or Google. The native sheet produces a signed
     * assertion; the server verifies it and decides which account it belongs to.
     */
    const loginWithProvider = useCallback(async (provider: Provider) => {
        try {
            const credential: SocialCredential = await signInWith(provider);
            const response = await fetch(`${API_URL}/api/auth/${provider}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    nonce: credential.nonce,
                    identityToken: credential.identityToken,
                    idToken: credential.idToken,
                    authorizationCode: credential.authorizationCode,
                    fullName: credential.fullName ?? undefined,
                }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) return { success: false, error: data.error || 'Sign-in failed' };
            await writeToken(data.token);
            setToken(data.token);
            setUser(data.user);
            return { success: true, isNewUser: Boolean(data.isNewUser) };
        } catch (error) {
            // A cancelled sheet is a normal outcome, not an error worth shouting about.
            if (isSignInCancelled(error)) return { success: false };
            return { success: false, error: error instanceof Error ? error.message : 'Sign-in failed' };
        }
    }, []);

    const refreshUser = useCallback(async () => {
        const current = await readToken();
        if (current) await fetchUser(current);
    }, []);

    const socialProviders = serverConfig && nativeReady
        ? availableProviders(serverConfig.providers, serverConfig.native)
        : [];

    return (
        <AuthContext.Provider value={{
            user,
            token,
            isAuthenticated: !!user && !!token,
            isLoading,
            sessionUnverified,
            login,
            register,
            logout,
            endSession,
            logoutError,
            updateBalance,
            loginWithProvider,
            serverConfig,
            socialProviders,
            refreshUser
        }}>
            {children}
        </AuthContext.Provider>
    );
};
